import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { DescuentosService } from '../../http/apiPricing/descuentos.service';
import { IBundle } from '../../interfaces/shared/bundle/IBundle';
import { DiscountService } from '../discount/discount.service';
import { SessionStorageService } from '../session-storage/session-storage.service';



@Injectable({ providedIn: 'root' })
export class StatusService {

  private subject = new BehaviorSubject<IBundle>(null);

  status: IBundle;
  bundle: IBundle[];
  currentIndexObject: number;

  /**
   * Initialize the currentIndexObject
   */
  constructor(
    private discountService: DiscountService,
    private sessionStorageService: SessionStorageService
  ) {
    this.currentIndexObject = 0;
  }
  
  /**
   * Get an observable of the subject that track the bundle
   * @returns An observable of the bundle that are currently used
   */
  getObservableContract(): Observable<IBundle> {
    return this.subject.asObservable();
  }

  /**
   * Computed property to retrieve the bundle array saved in memory
   */
  get bundleobject(): IBundle[] {
    return this.bundle;
  }

  /**
   * Get the bundle array
   * @returns The bundle array saved in memory
   */
  getBundle(): IBundle[] {
    return this.bundle;
  }

  /**
   * Set the bundle array in memory
   * @param bundle The bundle array to be saved
   */
  setBundle(bundle: IBundle[]): void {
    this.bundle = bundle || [];
    this.saveSession();
  }

  /**
   * Get the current bundle saved in memory
   * @returns The current bundle
   */
  getCurrent(): IBundle {
    return this.status;
  }

  /**
   * Get the current bundle index saved in memory
   * @returns The current bundle index
   */
  getIndex(): number {
    return this.currentIndexObject;
  }

  /**
   * Set the current bundle index in sessionStorage
   */
  saveIndex(): void {
    this.sessionStorageService.set('currentIndex', this.getIndex());
  }

  /**
   * Get the bundle and the bundle index from sessionStrorage and set them in memory
   */
  loadSession(): void {
    this.setBundle(this.sessionStorageService.get('bundle'))
    this.setCurrentIndexObject(this.sessionStorageService.get('currentIndex'))
  }

  /**
   * Set the bundle and the bundle index into sessionStrorage from the memory
   */
  saveSession(): void {
    this.sessionStorageService.set('bundle', this.getBundle())
    this.sessionStorageService.set('currentIndex', this.getIndex() ? this.getIndex() : 0);
  }

  /**
   * Set new bundle in memory and set it in the subject to track
   * @param newStatus The new bundle to be set
   */
  setStatus(newStatus: IBundle): void {
    this.status = { ...this.status, ...newStatus };
    this.subject.next(this.status);
  }

  /**
   * Load the bundle from sessionStorage and get the bundle in the current index
   * @returns The bundle in the current index
   */
  getCurrentObject(): IBundle {
    this.loadSession();
    return (this.bundle[this.currentIndexObject] || {});
  }

  /**
   * Set a bundle in the current index and save it into sessionStorage
   * @param bundle 
   */
  setCurrentObject(bundle: IBundle): void {
    this.bundle[this.currentIndexObject || 0] = { ...this.bundle[this.currentIndexObject], ...bundle };
    this.saveSession();
  }

  /**
   * Complete the bundle in the current index and save it into sessionStorage
   */
  completeCurrentObject(): void {
    this.bundle[this.currentIndexObject].completed = true;
    this.saveSession();
  }

  /**
   * Increments the current index and create a new bundle, then save the current index into sessionStorage
   */
  incrementCurrentIndexObject(): void {
    this.currentIndexObject++;

    if (this.currentIndexObject === this.bundle.length) {
      this.bundle.push({});
    }

    this.discountService.addDiscount(this.bundle);

    this.saveIndex()
  }

  /**
   * Set new current index into sessionStorage
   * @param index The index to be changed
   */
  setCurrentIndexObject(index: number): void {
    this.currentIndexObject = index;
    this.saveIndex();
  }

  /**
   * Delete the bundle in a index
   * @param index The index of the bundle to be deleted
   */
  deleteObject(index: number) {
    this.bundle.splice(index, 1);
    this.bundle.length > 0 ? this.currentIndexObject = this.bundle.length - 1 : this.currentIndexObject = 0;
    this.saveSession()
  }

  /**
   * Delete a property from a bundle object
   * @param property The property to be deleted
   */
  deletePropertyFromObject(property: keyof IBundle) {
    delete this.bundle[this.currentIndexObject][property];
    this.saveSession()
  }
}
